import * as React from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FamilySummaryFieldResponse } from "@/types/contract-family";
import { SummaryPromptsPayload } from "@/types/contract-family-pipeline";

interface Props {
  fields: FamilySummaryFieldResponse[];
  initialPrompts: Record<string, string>;
  extractionStarted: boolean;
  isLoading: boolean;
  onStartExtraction: () => Promise<void>;
  onGenerate: (payload: SummaryPromptsPayload) => Promise<void>;
  snapshotFirst?: boolean;
}

export const ExtractionSummaryStage = ({
  fields,
  initialPrompts,
  extractionStarted,
  isLoading,
  onStartExtraction,
  onGenerate,
  snapshotFirst = false,
}: Props) => {
  const { register, handleSubmit, reset, watch } = useForm<SummaryPromptsPayload>({
    defaultValues: { prompts_by_field_id: initialPrompts },
  });
  const promptsByFieldId = watch("prompts_by_field_id");

  React.useEffect(() => {
    reset({ prompts_by_field_id: initialPrompts });
  }, [initialPrompts, reset]);

  if (!extractionStarted && !snapshotFirst) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">Create the source collection with the fixed extraction template.</p>
        <Button disabled={isLoading} onClick={() => void onStartExtraction()}>
          {isLoading ? "Starting…" : "Start extraction"}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={handleSubmit(values =>
        onGenerate({
          prompts_by_field_id: Object.fromEntries(
            fields.map(field => [
              field.extraction_field_id,
              values.prompts_by_field_id[field.extraction_field_id]?.trim() ?? "",
            ]),
          ),
        }),
      )}
    >
      {snapshotFirst ? (
        <p className="text-sm text-slate-600">Generate summaries from the completed source snapshots.</p>
      ) : null}
      <p className="text-sm text-slate-600">Leave a field blank to use the value from the MASTER document.</p>
      <div className="grid gap-3 md:grid-cols-2">
        {fields.map(field => (
          <label key={field.extraction_field_id} className="block text-xs font-medium text-slate-700">
            {field.name}
            <Textarea
              className="mt-1 min-h-20 bg-white"
              aria-label={`Summary prompt for ${field.name}`}
              {...register(`prompts_by_field_id.${field.extraction_field_id}`)}
            />
            {(promptsByFieldId?.[field.extraction_field_id] ?? "").trim().length === 0 ? (
              <span className="mt-1 block text-xs text-slate-500">Using MASTER value</span>
            ) : null}
          </label>
        ))}
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={fields.length === 0 || isLoading}>
          {isLoading ? "Generating…" : "Generate summaries"}
        </Button>
      </div>
    </form>
  );
};
